import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Stack,
  TextField
} from "@mui/material";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import get from "lodash/get";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import BarChartComponent from "../../components/BarChart";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import branch from "../../services/branch";
import group from "../../services/group";
import report from "../../services/report";
import queryDefault from "../../utils/queryDefault";

export default function AllReport() {
  const [valueFrom, setValueFrom] = useState<Dayjs | null>(dayjs(new Date()));
  const [valueTo, setValueTo] = useState<Dayjs | null>(dayjs(new Date()));

  const defaultValues = {
    group_ids: [] as number[],
    branch_ids: [] as number[],
    date_from: dayjs(new Date()).format('YYYY-MM-DD'),
    date_to: dayjs(new Date()).format('YYYY-MM-DD'),
    is_weekday: true,
    is_weekend: true,
  };

  const form = useForm({ defaultValues });

  const watchedGroupIds = form.watch("group_ids");
  const watchedBranchIds = form.watch("branch_ids");
  const watchedIsWeekday = form.watch("is_weekday");
  const watchedIsWeekend = form.watch("is_weekend");

  const options = useMemo(
    () => ({
      date_from: valueFrom?.isValid() ? valueFrom.format("YYYY-MM-DD") : defaultValues.date_from,
      date_to: valueTo?.isValid() ? valueTo.format("YYYY-MM-DD") : defaultValues.date_to,
      group_id: watchedGroupIds?.length ? watchedGroupIds.join(",") : undefined,
      branch_id: watchedBranchIds?.length ? watchedBranchIds.join(",") : undefined,
      is_weekday: typeof watchedIsWeekday === "boolean" ? watchedIsWeekday : true,
      is_weekend: typeof watchedIsWeekend === "boolean" ? watchedIsWeekend : true,
    }),
    [
      defaultValues.date_from,
      defaultValues.date_to,
      valueFrom,
      valueTo,
      watchedGroupIds,
      watchedBranchIds,
      watchedIsWeekday,
      watchedIsWeekend,
    ]
  );

  const viewAllReport = useQuery(
    ["viewreport", options],
    () => {
      return report.viewAllReport(options);
    },
    {
      ...queryDefault,
    }
  );

  const groups = useQuery(
    ["groups"],
    () => {
      return group.getAllBranchGroup({ keyword: "" });
    },
    {
      ...queryDefault,
    }
  );

  const branches = useQuery(
    ["branches", { keyword: "" }],
    () => {
      return branch.getAllBranch({ keyword: "" });
    },
    {
      ...queryDefault,
    }
  );

  const onSubmit = () => {
    report.getAllReport(options);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label={"All Report"}
        breadcrumbs={[
          { label: "Report", href: "/app/report" },
          {
            label: "All Report",
            href: "/app/report/all",
          },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl margin="normal" fullWidth>
                <FormLabel>Group</FormLabel>
                <Controller
                  name="group_ids"
                  control={form.control}
                  render={() => (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={get(groups, "data.data", [])}
                      value={get(groups, "data.data", []).filter((item: any) =>
                        form.getValues().group_ids.includes(Number(item.id))
                      )}
                      onChange={(_, selectedOptions: any[]) => {
                        form.setValue(
                          "group_ids",
                          selectedOptions.map((item: any) => Number(item.id))
                        );
                      }}
                      getOptionLabel={(option: any) => option.name || ""}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                      filterOptions={(options, { inputValue }) =>
                        options.filter((option: any) =>
                          String(option.name || "").toLowerCase().includes(inputValue.toLowerCase())
                        )
                      }
                      renderInput={(params) => (
                        <TextField {...params} size="small" placeholder="All Group" />
                      )}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl margin="normal" fullWidth>
                <FormLabel>Branch</FormLabel>
                <Controller
                  name="branch_ids"
                  control={form.control}
                  render={() => (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={get(branches, "data.data", [])}
                      value={get(branches, "data.data", []).filter((item: any) =>
                        form.getValues().branch_ids.includes(Number(item.id))
                      )}
                      onChange={(_, selectedOptions: any[]) => {
                        form.setValue(
                          "branch_ids",
                          selectedOptions.map((item: any) => Number(item.id))
                        );
                      }}
                      getOptionLabel={(option: any) => option.name || ""}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                      filterOptions={(options, { inputValue }) =>
                        options.filter((option: any) =>
                          String(option.name || "").toLowerCase().includes(inputValue.toLowerCase())
                        )
                      }
                      renderInput={(params) => (
                        <TextField {...params} size="small" placeholder="All Branch" />
                      )}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl margin="normal" fullWidth>
                <FormLabel>Start Date</FormLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    inputFormat="DD/MM/YYYY"
                    views={["day", "month", "year"]}
                    value={valueFrom}
                    onChange={newValue => {
                      setValueFrom(newValue);
                    }}
                    renderInput={props => <TextField {...props} />}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl margin="normal" fullWidth>
                <FormLabel>End Date</FormLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    inputFormat="DD/MM/YYYY"
                    views={["day", "month", "year"]}
                    value={valueTo}
                    onChange={newValue => {
                      setValueTo(newValue);
                    }}
                    renderInput={props => <TextField {...props} />}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Controller
              name="is_weekday"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={<Checkbox checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />}
                  label="Weekday"
                />
              )}
            />

            <Controller
              name="is_weekend"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={<Checkbox checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />}
                  label="Weekend"
                />
              )}
            />
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Button
              style={{ marginBottom: "20px" }}
              variant="outlined"
              onClick={form.handleSubmit(onSubmit)}
            >
              Generate Report
            </Button>
          </Box>
        </Box>
        <BarChartComponent data={get(viewAllReport, "data.data", [])} />
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
